/**
 * The console's visual language, as named constants.
 *
 * Exported rather than inlined so they are greppable and cannot drift screen to screen — the
 * previous console had nine variants of the same white rounded card, each announcing its own
 * edge, which is what made a money page read like a settings page.
 *
 * ONE RESERVED ALERT COLOUR: rose. **There is no green.** `THE BOOKS BALANCE` renders in
 * slate and says so in words. The old console used emerald for good, blue for in-flight,
 * amber for partial, rose for bad and slate for dead — five colours, so rose carried no more
 * weight than emerald and the eye did not stop on it. When slate is the only colour on the
 * screen, the one rose thing is the only thing you see. A green success box on a screen that
 * is green ninety-nine days in a hundred is a box the operator stops reading on day three,
 * and day one hundred is a payout that should not have gone out.
 *
 * Colour is never the only carrier: every alarm also carries the word and `role="alert"`.
 *
 * MONO IS FOR IDENTIFIERS, `tabular-nums` IS FOR MONEY. The house already uses
 * `font-mono text-xs` for ids; keeping the split means a payout id and a payout amount can
 * never be confused at a glance, which is not a distinction a money screen can afford to
 * blur.
 */
import React from "react";

import { formatMoneyLedger } from "../../lib/formatMoney";
import { useLanguage, useT } from "../../lib/LanguageContext";

/** The sheet. White, no border, no `rounded-2xl`, no shadow — the layout's own
 *  `#f8f9fa` page background separates it. One sheet with rules on it, not nine cards. */
export const SHEET = "bg-white max-w-5xl mx-auto px-5 sm:px-8 py-7 text-slate-800";

/** The same sheet, for the one screen that is a WIDE TABLE rather than a document.
 *
 * `max-w-5xl` is a prose measure: it exists so a ledger reads like a page. The payments
 * console is nine columns of cross-tenant money — practice, reason, item, buyer, status
 * and three figures — and inside the prose sheet it measured 1396 px of table in a 958 px
 * card, i.e. 438 px of every row hidden behind a horizontal scrollbar AT EVERY VIEWPORT,
 * including 2200 px. Trimming columns cannot recover 438 px without deleting the answers
 * the screen exists to give, so this screen gets the width instead. Named and opted into
 * per screen, never applied globally: widening the sheet for the ledgers would undo the
 * "one sheet, not nine cards" decision this file is built on. */
export const WIDE_SHEET =
  "bg-white max-w-screen-2xl mx-auto px-5 sm:px-8 py-7 text-slate-800";

/** Row separator. 10% slate-900 rather than `border-slate-200`, which at nine cards' worth
 *  of repetition becomes grey noise. */
export const RULE = "border-t border-slate-900/10";

/** Used EXACTLY ONCE per screen, under its title — the way a ledger underscores its column
 *  headings. It is also what replaces the active-tab chrome: the strong rule says where you
 *  are, so the nav does not need a filled pill to. */
export const RULE_STRONG = "border-t-2 border-slate-900";

export const COLHEAD =
  "text-[11px] uppercase tracking-[0.08em] text-slate-400 font-medium text-left";
export const FIGURE = "block text-right tabular-nums text-slate-900 whitespace-nowrap";
export const FIGURE_MUTED = "block text-right tabular-nums text-slate-500 whitespace-nowrap";
/** `null` is not `0`; its dash marker aligns with the numbers around it. */
export const FIGURE_NULL = "block text-right tabular-nums text-slate-300 whitespace-nowrap";
export const MUTED = "text-xs text-slate-500";
export const IDENT = "font-mono text-xs text-slate-500";

export const ALARM_TEXT = "text-rose-700";
export const ALARM_ROW = "border-l-2 border-rose-700 pl-3 text-rose-700";
export const UNKNOWN_ROW = "border-l-2 border-slate-300 pl-3 text-slate-500 italic";

export const NAV_ON = "text-slate-900 border-b-2 border-slate-900 pb-1";
export const NAV_OFF =
  "text-slate-400 hover:text-slate-700 border-b-2 border-transparent pb-1";

/**
 * A money cell in the ledger style ("1 557,50 CAD" in fr). Checks `cents == null` BEFORE
 * `formatMoneyLedger`, because it prints a missing amount as 0 — so the "null renders —, never 0"
 * law is enforced here, once, instead of at forty call sites.
 *
 * BLOCK-LEVEL, and it owns its right alignment and fixed-width digits: `text-align` does
 * nothing on an inline element, so an inline span under a right-aligned `<th>` left every
 * value left-aligned. Callers must not wrap it, or add `text-right` / `tabular-nums` to the
 * `<td>`, to align it. In a flex row it is a flex item and sits where the row puts it.
 */
export function Figure({ cents, currency, muted = false, alarm = false, testId }) {
  const { lang } = useLanguage();
  if (cents == null) {
    return <span className={FIGURE_NULL} data-testid={testId}>—</span>;
  }
  const cls = alarm ? `${FIGURE} ${ALARM_TEXT}` : muted ? FIGURE_MUTED : FIGURE;
  return <span className={cls} data-testid={testId}>{formatMoneyLedger(cents, currency, lang)}</span>;
}

/** A screen title with its single strong rule. */
export function ScreenTitle({ children, aside }) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">{children}</h2>
        {aside}
      </div>
      <div className={`${RULE_STRONG} mt-2`} />
    </div>
  );
}

/** A small-caps section label, for the strip and for grouped panels. */
export function SectionLabel({ children }) {
  return <div className={COLHEAD}>{children}</div>;
}

/**
 * A status as TEXT plus a rule, never a coloured pill. A bad status swaps to the alarm
 * colour and changes nothing else, which is what keeps the one rose thing on the page the
 * thing you actually need to look at.
 */
export function StatusLine({ label, bad = false, testId }) {
  return (
    <span
      className={`text-xs uppercase tracking-[0.06em] ${bad ? ALARM_TEXT : "text-slate-500"}`}
      data-testid={testId}>
      {label}
    </span>
  );
}

/**
 * One paragraph, before every table, saying in plain words what is in the list and what is
 * NOT. "Every screen explains the criteria it applies", made structural rather than left to
 * whoever writes the next screen — the payout queue names its four eligibility tests, the
 * commission screen says first-match-wins and that a change never restates a captured
 * payment.
 */
export function Criteria({ children }) {
  return <p className="text-xs text-slate-500 max-w-prose leading-relaxed">{children}</p>;
}


/**
 * Resolve a FIELD-ERROR key, whichever namespace it lives in.
 *
 * This exists because the same mistake has now been made four times in this campaign: a key
 * resolved with the wrong translator renders as the literal key, because `lookup()` answers
 * the key verbatim on a miss. Three instances were `gw.error.*` (which lives in `common`)
 * handed the `superadmin` translator; the fourth was the Adjustments form resolving
 * `gw.valid.*` (which lives in `superadmin`) with the `common` one — so a patient-facing
 * money form told the operator `gw.valid.reasonMin5`.
 *
 * The keys genuinely live in two namespaces and both are correct: `gw.valid.*` is
 * console-specific copy, while `validation.*` / `errors.*` are the shared builders in
 * `lib/validation.js` that every plane uses. So the resolver routes by prefix rather than
 * asking each caller to remember, and no form needs to know the difference again.
 */
export function useFieldErrorText() {
  const t = useT("superadmin");
  const tc = useT("common");
  return (key) => {
    if (!key) return "";
    return /^(validation\.|errors\.|gw\.error\.|gw\.rule\.)/.test(key) ? tc(key) : t(key);
  };
}
